import CyrillicToTranslit from "cyrillic-to-translit-js"
import { reduce } from "lodash"
import type { TUser } from "@/types"

// @ts-expect-error
const cyrillicToTranslit = new CyrillicToTranslit()

export function prepareQuery(query: string): ISearchQueries {
  if (!query) return { query }
  const search = query.toLowerCase()
  // biome-ignore lint: I need to use a regex here
  const isLatin = /^[\x00-\x7F]*$/.test(search)
  if (isLatin)
    return {
      query,
      latin: search,
      alternate: cyrillicToTranslit.reverse(search),
    }
  return {
    query,
    alternate: search,
    latin: cyrillicToTranslit.transform(search),
  }
}

export interface ISearchQueries {
  query?: string
  latin?: string
  alternate?: string
}

export interface ISearchUserLikeItem {
  email?: string
  full_name?: string | null
  position?: string | null
}

export function filterUser({ latin, alternate }: ISearchQueries, item: ISearchUserLikeItem) {
  if (!latin && !alternate) return true
  const name = item.full_name?.toLowerCase()
  if (name && ((latin && name.includes(latin)) || (alternate && name.includes(alternate)))) return true

  const mail = item.email?.toLowerCase()
  if (mail && ((latin && mail.includes(latin)) || (alternate && mail.includes(alternate)))) return true

  const pos = item.position?.toLowerCase()
  if (pos && ((latin && pos.includes(latin)) || (alternate && pos.includes(alternate)))) return true

  return false
}

export function getManagers(users: TUser[]): TUser[] {
  const managerIds = new Set(users.filter((u) => u.manager_id).map((u) => u.manager_id))
  return users.filter((u) => managerIds.has(u.id))
}

const roleOrder: Record<TUser["role"], number> = {
  owner: 0,
  admin: 1,
  manager: 2,
  user: 3,
}

export function sortUsersByRole(users: TUser[]): TUser[] {
  return [...users].sort((a, b) => {
    const byRole = (roleOrder[a.role] ?? roleOrder.user) - (roleOrder[b.role] ?? roleOrder.user)
    if (byRole !== 0) return byRole
    return (a.full_name || a.name).localeCompare(b.full_name || b.name)
  })
}

export function sortUsersByManager(users: TUser[]): TUser[] {
  const byId = reduce(
    users,
    (acc, u) => {
      acc[u.id] = u
      return acc
    },
    {} as Record<string, TUser>,
  )
  const children = new Map<string | null, TUser[]>()

  for (const u of users) {
    const manager_id = u.manager_id && byId[u.manager_id] ? u.manager_id : null
    if (!children.has(manager_id)) {
      children.set(manager_id, [])
    }
    children.get(manager_id)?.push(u)
  }

  const result: TUser[] = []

  function addWithChildren(user: TUser) {
    result.push(user)
    const subs = children.get(user.id) || []
    for (const sub of subs) addWithChildren(sub)
  }

  const roots = children.get(null) || []

  for (const r of roots) addWithChildren(r)

  return result
}
